import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Form,
  Icon,
  Input,
  InputNumber,
  Row,
  Select,
  message,
} from 'antd';
import moment from 'moment';
import UploadFiles from 'components/UploadFiles/UploadFiles';
import { upsertExpenses } from '../api/ownerReporting';
import {
  LUMPSUM_TABS,
  TOILETRY_TYPES,
  emptyLine,
  hydrateByUnit,
  cleanUnitRowsForSave,
  rollupByUnitExpenses,
  tabTotalsFromUnitRows,
} from './expensesByUnit';
import styles from './ExpensesFormDrawer.module.css';

const formatMoney = n => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0.00';
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const packageIdOf = pkg => {
  if (!pkg) return '';
  if (typeof pkg === 'object') return String(pkg._id || pkg);
  return String(pkg);
};

const ExpensesFormDrawer = ({
  visible,
  onClose,
  onSaved,
  hostId,
  packages = [],
  hostUnits = [],
  initialRecord = null,
  defaultYear,
  defaultMonth,
}) => {
  const isEdit = !!(initialRecord && initialRecord._id);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState('others');
  const [meta, setMeta] = useState({
    servicePackage: undefined,
    year: defaultYear,
    month: defaultMonth,
  });
  const [unitRows, setUnitRows] = useState([]);
  const [othersByOwner, setOthersByOwner] = useState([]);

  const selectedPackage = useMemo(
    () => (packages || []).find(pkg => String(pkg._id) === String(meta.servicePackage)),
    [packages, meta.servicePackage]
  );

  const applyPackageUnits = (servicePackageId, recordExpenses) => {
    const pkg = (packages || []).find(item => String(item._id) === String(servicePackageId));
    setUnitRows(
      hydrateByUnit({
        packageUnits: pkg ? pkg.units : [],
        hostUnits,
        recordExpenses: recordExpenses || {},
      })
    );
  };

  useEffect(
    () => {
      if (!visible) return;
      setCurrentTab('others');
      if (initialRecord) {
        const servicePackage = packageIdOf(initialRecord.servicePackage) || undefined;
        setMeta({
          servicePackage,
          year: Number(initialRecord.year) || defaultYear,
          month: Number(initialRecord.month) || defaultMonth,
        });
        setOthersByOwner(
          initialRecord.expenses && Array.isArray(initialRecord.expenses.othersByOwner)
            ? initialRecord.expenses.othersByOwner
            : []
        );
        applyPackageUnits(servicePackage, initialRecord.expenses);
        return;
      }
      setMeta({
        servicePackage: undefined,
        year: defaultYear,
        month: defaultMonth,
      });
      setOthersByOwner([]);
      setUnitRows([]);
    },
    // Hydrate once when the drawer opens. Package changes on create go through onPackageChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, initialRecord, defaultYear, defaultMonth]
  );

  const tabTotals = useMemo(() => tabTotalsFromUnitRows(unitRows), [unitRows]);

  const grandTotal = useMemo(
    () =>
      Object.keys(tabTotals).reduce((sum, key) => sum + (Number(tabTotals[key]) || 0), 0) +
      (othersByOwner || []).reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [tabTotals, othersByOwner]
  );

  const updateUnit = (unitIdx, patch) => {
    setUnitRows(prev => {
      const next = prev.slice();
      next[unitIdx] = Object.assign({}, next[unitIdx], patch);
      return next;
    });
  };

  const setLumpsum = (unitIdx, key) => val => updateUnit(unitIdx, { [key]: Number(val) || 0 });

  const addUnitAttachment = (unitIdx, key) => file => {
    setUnitRows(prev => {
      const next = prev.slice();
      const row = Object.assign({}, next[unitIdx]);
      const files = Array.isArray(row[key]) ? row[key].slice() : [];
      files.push({ name: file.fileKey, key: file.fileKey });
      row[key] = files;
      next[unitIdx] = row;
      return next;
    });
  };

  const deleteUnitAttachment = (unitIdx, key) => index => () => {
    setUnitRows(prev => {
      const next = prev.slice();
      const row = Object.assign({}, next[unitIdx]);
      row[key] = (row[key] || []).filter((_, i) => i !== index);
      next[unitIdx] = row;
      return next;
    });
  };

  const updateLine = (unitIdx, listKey, lineIdx, field, value) => {
    setUnitRows(prev => {
      const next = prev.slice();
      const row = Object.assign({}, next[unitIdx]);
      const list = (row[listKey] || []).slice();
      list[lineIdx] = Object.assign({}, list[lineIdx], {
        [field]: field === 'amount' ? Number(value) || 0 : value,
      });
      row[listKey] = list;
      next[unitIdx] = row;
      return next;
    });
  };

  const addLine = (unitIdx, listKey, withDate) => {
    setUnitRows(prev => {
      const next = prev.slice();
      const row = Object.assign({}, next[unitIdx]);
      row[listKey] = (row[listKey] || []).concat([emptyLine(withDate)]);
      next[unitIdx] = row;
      return next;
    });
  };

  const removeLine = (unitIdx, listKey, lineIdx) => {
    setUnitRows(prev => {
      const next = prev.slice();
      const row = Object.assign({}, next[unitIdx]);
      const list = (row[listKey] || []).slice();
      if (list.length <= 1) {
        list[0] = emptyLine(listKey === 'othersByHost');
      } else {
        list.splice(lineIdx, 1);
      }
      row[listKey] = list;
      next[unitIdx] = row;
      return next;
    });
  };

  const addLineAttachment = (unitIdx, listKey, lineIdx) => file => {
    setUnitRows(prev => {
      const next = prev.slice();
      const row = Object.assign({}, next[unitIdx]);
      const list = (row[listKey] || []).slice();
      const line = Object.assign({}, list[lineIdx] || emptyLine(listKey === 'othersByHost'));
      const attachments = Array.isArray(line.expensesAttachment) ? line.expensesAttachment.slice() : [];
      attachments.push({ name: file.fileKey, key: file.fileKey });
      line.expensesAttachment = attachments;
      list[lineIdx] = line;
      row[listKey] = list;
      next[unitIdx] = row;
      return next;
    });
  };

  const deleteLineAttachment = (unitIdx, listKey, lineIdx, fileIndex) => () => {
    setUnitRows(prev => {
      const next = prev.slice();
      const row = Object.assign({}, next[unitIdx]);
      const list = (row[listKey] || []).slice();
      const line = Object.assign({}, list[lineIdx] || {});
      line.expensesAttachment = (line.expensesAttachment || []).filter((_, i) => i !== fileIndex);
      list[lineIdx] = line;
      row[listKey] = list;
      next[unitIdx] = row;
      return next;
    });
  };

  const onPackageChange = val => {
    setMeta(prev => Object.assign({}, prev, { servicePackage: val }));
    applyPackageUnits(val, {});
  };

  const onSave = async () => {
    if (!meta.servicePackage) {
      message.error('Service package is required');
      return;
    }
    if (!meta.year || !meta.month) {
      message.error('Year and month are required');
      return;
    }
    if (!unitRows.length) {
      message.error('This package has no units');
      return;
    }
    setSaving(true);
    try {
      const byUnit = cleanUnitRowsForSave(unitRows);
      const expenses = rollupByUnitExpenses({
        byUnit,
        othersByOwner,
      });
      await upsertExpenses(hostId, {
        servicePackage: meta.servicePackage,
        year: Number(meta.year),
        month: Number(meta.month),
        units: byUnit.map(row => row.unitId),
        expenses,
      });
      message.success('Saved');
      if (onSaved) onSaved(Number(meta.year));
      if (onClose) onClose();
    } catch (e) {
      message.error((e && e.message) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const tabList = [
    { key: 'others', tab: 'Others (' + formatMoney(tabTotals.others) + ')' },
    ...LUMPSUM_TABS.map(({ key, label }) => ({
      key,
      tab: label + ' (' + formatMoney(tabTotals[key] || 0) + ')',
    })),
    { key: 'toiletry', tab: 'Toiletry (' + formatMoney(tabTotals.toiletry) + ')' },
  ];

  const renderLumpsumTab = key => (
    <div>
      <h3 className={styles.tabTitle}>{LUMPSUM_TABS.filter(tab => tab.key === key)[0].label} Expense</h3>
      {unitRows.map((unit, unitIdx) => (
        <Row className={styles.inputRow} key={unit.unitId + '-' + key} gutter={12} type="flex" align="top">
          <Col span={5} xs={24} sm={24} md={24} lg={5} xl={4}>
            <span className={styles.unitLabel}>{unit.unitNo}</span>
          </Col>
          <Col span={8} xs={24} sm={12} md={12} lg={8} xl={6}>
            <Input
              name={key}
              addonBefore="Amount"
              placeholder="0"
              type="number"
              step="0.01"
              value={unit[key]}
              onChange={e => setLumpsum(unitIdx, key)(e.target.value)}
            />
          </Col>
          <Col span={11} xs={24} sm={12} md={12} lg={11} xl={10}>
            <UploadFiles
              isPrivate
              files={unit[key + '_attachment'] || []}
              onUploadFinish={addUnitAttachment(unitIdx, key + '_attachment')}
              onFileDelete={deleteUnitAttachment(unitIdx, key + '_attachment')}
              checkIsAdmin
            />
          </Col>
        </Row>
      ))}
    </div>
  );

  const renderLineTab = (listKey, withDate, isToiletry) => (
    <div>
      <h3 className={styles.tabTitle}>{isToiletry ? 'Toiletry Sets Expense' : 'Others Expense'}</h3>
      {unitRows.map((unit, unitIdx) => (
        <Row className={styles.inputRow} key={unit.unitId + '-' + listKey} gutter={12} type="flex" align="top">
          <Col span={5} xs={24} sm={24} md={24} lg={5} xl={4}>
            <span className={styles.unitLabel}>{unit.unitNo}</span>
          </Col>
          <Col span={19} xs={24} sm={24} md={24} lg={19} xl={20}>
            {(unit[listKey] || []).map((line, lineIdx) => (
              <Row className={styles.lineRow} key={listKey + '-' + unitIdx + '-' + lineIdx} gutter={8} type="flex" align="top">
                <Col span={8} xs={24} sm={24} md={12} lg={8} xl={6}>
                  {isToiletry ? (
                    <Select
                      style={{ width: '100%' }}
                      placeholder={'Toiletry type ' + (lineIdx + 1)}
                      value={line.label || undefined}
                      onChange={val => updateLine(unitIdx, listKey, lineIdx, 'label', val)}
                    >
                      {TOILETRY_TYPES.map(t => (
                        <Select.Option key={t.value} value={t.value}>
                          {t.label}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      addonBefore={'Item ' + (lineIdx + 1)}
                      placeholder="Item name"
                      value={line.label}
                      onChange={e => updateLine(unitIdx, listKey, lineIdx, 'label', e.target.value)}
                    />
                  )}
                </Col>
                <Col span={7} xs={24} sm={12} md={12} lg={7} xl={5}>
                  <Input
                    addonBefore="Amount"
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={line.amount}
                    onChange={e => updateLine(unitIdx, listKey, lineIdx, 'amount', e.target.value)}
                  />
                </Col>
                {withDate && (
                  <Col span={8} xs={24} sm={12} md={12} lg={8} xl={6}>
                    <DatePicker
                      style={{ width: '100%' }}
                      value={line.transactionDate ? moment(line.transactionDate) : null}
                      onChange={(_d, dateString) => updateLine(unitIdx, listKey, lineIdx, 'transactionDate', dateString)}
                    />
                  </Col>
                )}
                <Col span={withDate ? 8 : 8} xs={22} sm={20} md={20} lg={8} xl={6}>
                  <UploadFiles
                    isPrivate
                    files={line.expensesAttachment || []}
                    onUploadFinish={addLineAttachment(unitIdx, listKey, lineIdx)}
                    onFileDelete={fileIndex => deleteLineAttachment(unitIdx, listKey, lineIdx, fileIndex)}
                    checkIsAdmin
                  />
                </Col>
                <Col span={1} xs={2} sm={4} md={4} lg={1} xl={1}>
                  <Button
                    icon="minus"
                    type="danger"
                    shape="circle"
                    size="small"
                    onClick={() => removeLine(unitIdx, listKey, lineIdx)}
                  />
                </Col>
              </Row>
            ))}
            <Button type="dashed" className={styles.addField} onClick={() => addLine(unitIdx, listKey, withDate)}>
              <Icon type="plus" /> Add field
            </Button>
          </Col>
        </Row>
      ))}
    </div>
  );

  const renderTabBody = () => {
    if (!meta.servicePackage) {
      return <div className={styles.emptyHint}>Select a service package to enter expenses unit by unit.</div>;
    }
    if (!unitRows.length) {
      return <div className={styles.emptyHint}>This package has no units.</div>;
    }
    if (currentTab === 'others') return renderLineTab('othersByHost', true, false);
    if (currentTab === 'toiletry') return renderLineTab('toiletry', false, true);
    return renderLumpsumTab(currentTab);
  };

  return (
    <Drawer
      title={isEdit ? 'Update Expenses' : 'Create Expenses'}
      width="90%"
      placement="right"
      visible={visible}
      onClose={onClose}
      destroyOnClose
      wrapClassName={styles.wrap}
      bodyStyle={{ paddingBottom: 80 }}
    >
      <Form layout="vertical">
        <Row gutter={16}>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item label="Service Package" required>
              <Select
                style={{ width: '100%' }}
                value={meta.servicePackage}
                disabled={isEdit}
                showSearch
                optionFilterProp="children"
                onChange={onPackageChange}
                placeholder="Select service package"
              >
                {(packages || []).map(p => (
                  <Select.Option key={String(p._id)} value={String(p._id)}>
                    {p.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Form.Item label="Year" required>
              <InputNumber
                style={{ width: '100%' }}
                value={meta.year}
                disabled={isEdit}
                onChange={val => setMeta(prev => Object.assign({}, prev, { year: val }))}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Form.Item label="Month" required>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                max={12}
                value={meta.month}
                disabled={isEdit}
                onChange={val => setMeta(prev => Object.assign({}, prev, { month: val }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Card size="small" className={styles.headerCard}>
        <Row gutter={8}>
          <Col xs={24} md={12}>
            <div>
              <span className={styles.metaLabel}>Service Package:</span>
              <span className={styles.metaValue}>{(selectedPackage && selectedPackage.name) || '—'}</span>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div>
              <span className={styles.metaLabel}>Total Expenses:</span>
              <span className={styles.metaValue}>{formatMoney(grandTotal)}</span>
            </div>
          </Col>
        </Row>
      </Card>

      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={'Total: ' + formatMoney(grandTotal)}
        tabList={tabList}
        activeTabKey={currentTab}
        onTabChange={setCurrentTab}
      >
        {renderTabBody()}
      </Card>

      <div className={styles.footer}>
        <Button onClick={onClose} style={{ marginRight: 8 }}>
          Cancel
        </Button>
        <Button type="primary" loading={saving} onClick={onSave}>
          Save All Expenses
        </Button>
      </div>
    </Drawer>
  );
};

export default ExpensesFormDrawer;
